import React, { useState } from "react";
import { Zap, X } from "lucide-react";
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useGlobalSearch } from "@/hooks/useGlobalSearch";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import SearchResults from "./SearchResults";

export const SearchPopover: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const { results, isLoading } = useGlobalSearch(query);
  const navigate = useNavigate();
  
  const handleNavigate = (type: string, id: string) => {
    setOpen(false);
    setQuery("");
    
    let url = '';
    switch (type) {
      case 'families':
        url = `/admin/families/${id}`;
        break;
      case 'students':
        url = `/admin/students/${id}`;
        break;
      case 'classes':
        url = `/admin/classes/${id}`;
        break;
      case 'events':
        url = `/admin/events/${id}`;
        break;
      case 'staffMembers':
        url = `/admin/staff-profile/${id}`;
        break;
      default:
        url = `/admin`;
    }
    
    try {
      console.log(`Navigating to ${type} with ID ${id} at URL: ${url}`);
      navigate(url);
    } catch (error) {
      console.error("Navigation error:", error);
      toast.error("Unable to navigate to the selected item.");
    }
  };
  
  return (
    <div className="relative w-full max-w-sm">
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            className={
              `flex h-11 w-full items-center rounded-full border border-primary/60 bg-background px-4 py-2 text-base ring-offset-background cursor-pointer shadow-sm transition-all duration-200
              hover:shadow-lg focus-within:shadow-lg focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/40`
            }
            tabIndex={0}
            aria-label="Open global search"
            onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setOpen(true); }}
          >
            <Zap className={`mr-3 h-5 w-5 shrink-0 text-yellow-500 drop-shadow-[0_0_6px_rgba(251,191,36,0.6)] transition-transform duration-200 group-hover:scale-110 ${open ? 'animate-pulse' : ''}`} />
            <span className="text-muted-foreground font-medium">Search families, students, staff...</span>
          </div>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[400px] bg-white/80 backdrop-blur-md shadow-2xl rounded-2xl border-0 animate-fade-in-slide" align="start">
          <Command>
            <div className="flex flex-col gap-1">
              <div className="flex items-center border-b px-4 py-2">
                <Zap className={`mr-3 h-5 w-5 shrink-0 text-yellow-500 drop-shadow-[0_0_6px_rgba(251,191,36,0.6)] transition-transform duration-200 ${open || query ? 'animate-pulse' : ''}`} />
                <CommandInput
                  placeholder="Type to search across your organization..."
                  value={query}
                  onValueChange={setQuery}
                  className="flex h-12 w-full rounded-full bg-transparent py-3 text-base outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-50"
                  autoFocus
                />
                {query && (
                  <X
                    className="h-5 w-5 shrink-0 opacity-50 cursor-pointer ml-2"
                    onClick={() => setQuery("")}
                  />
                )}
              </div>
              <div className="flex justify-end pr-6 pb-1">
                <span className="text-xs text-yellow-600 bg-yellow-100 rounded-full px-2 py-0.5 font-semibold shadow-sm">⚡ Powered by AI</span>
              </div>
            </div>
            <CommandList className="max-h-[350px] overflow-y-auto p-2">
              {isLoading ? (
                <div className="py-8 text-center text-base">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mr-3"></div>
                    <span>Searching...</span>
                  </div>
                </div>
              ) : (
                <>
                  {!query && (
                    <div className="py-8 text-center text-base text-muted-foreground">
                      Type to start searching across your organization...
                    </div>
                  )}
                  {query && !isLoading && (
                    <SearchResults 
                      results={results}
                      closeDialog={() => setOpen(false)} 
                      query={query}
                      onSelect={handleNavigate} 
                      isLoading={isLoading} 
                    />
                  )}
                </>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  );
};
